<template>
  <PageContainer title="Buscar Cobranças" data-testid="buscar-cobrancas-page">
    <Box space="medium" class="cobrancas-container" data-testid="cobrancas-container">
      <form @submit.prevent="handleSubmit" data-testid="buscar-cobrancas-form">
        <div class="inputs" data-testid="form-inputs">
          <div class="inputs-row" data-testid="inputs-row">
            <Select v-if="idRole === 6" :options="schemaOptions" legend="Auto escola"
              placeholder="Selecione uma auto escola" :modelValue="schema"
              @update:modelValue="$event => (schema = $event)" class="input-full" data-testid="select-autoescola" />

            <Select :options="statusOptions" legend="Status" placeholder="Selecione um status" :modelValue="status"
              @update:modelValue="$event => (status = $event)" class="input-full" data-testid="select-status" />

            <Period legend="Período de emissão" :firstModelValue="initialDate" :secondModelValue="finalDate"
              @update:firstModelValue="$event => (this.initialDate = $event)"
              @update:secondModelValue="$event => (this.finalDate = $event)" :disabled="loading"
              data-testid="period-emissao" />
          </div>
        </div>

        <div class="buttons" data-testid="form-buttons">
          <Button type="button" @handle:click="clearInputs" text="Limpar" :fill="false" color="danger"
            :disabled="loading" data-testid="btn-limpar">
            <CloseCircleOutline :size="18" />
          </Button>

          <Button type="submit" text="Consultar" color="primary" :disabled="loading" data-testid="btn-consultar">
            <Magnify :size="18" />
          </Button>
        </div>
      </form>

      <div class="loading" v-if="loading" data-testid="loading-area">
        <LoadingSpinner data-testid="loading-spinner" />
      </div>

      <div class="table-container" v-else-if="cobrancas.length > 0" data-testid="table-container">
        <Paginate :total-pages="paginationData.lastPage" :current-page="paginationData.currentPage"
          @page-changed="fetchCobrancas" data-testid="pagination" />

        <Table :headers="headers" data-testid="cobrancas-table">
          <tr v-for="(cobranca, index) in cobrancas" :key="cobranca.id" :data-testid="`cobranca-row-${index}`">
            <td v-if="idRole === 6" data-testid="autoescola-cell">{{ cobranca.cfc.nome_fantasia }}</td>
            <td data-testid="valor-cell">{{ utils.formatarValor(cobranca.amount / 100) }}</td>
            <td v-if="cobranca.boleto.status === 'pending'" style="color: #FABF10;" data-testid="status-cell-pending">
              Pendente</td>
            <td v-else-if="cobranca.boleto.status === 'paid'" style="color: #16A34A;" data-testid="status-cell-paid">
              Pago</td>
            <td v-else-if="cobranca.boleto.status === 'cancelled'" style="color: #CA2222;"
              data-testid="status-cell-cancelled">
              <Tooltip
                :text="cancelamentoInfos[cobranca.id] ? `Cancelado por: ${cancelamentoInfos[cobranca.id].usuario} na data ${cancelamentoInfos[cobranca.id].data} às ${cancelamentoInfos[cobranca.id].hora}` : 'Cancelado (carregando informações...)'"
                position="top" custom-class="warning" data-testid="tooltip-cancelamento-info"
                @mouseenter="loadCancelamentoInfo(cobranca.id)">
                <span class="status-cancelado" data-testid="status-cancelado-text">Cancelado</span>
              </Tooltip>
            </td>
            <td v-else-if="cobranca.boleto.status === 'overdue'" style="color: #CA2222;"
              data-testid="status-cell-overdue">Vencido</td>
            <td data-testid="vencimento-cell">{{ utils.formatOnlyDate(cobranca.boleto.due_date.split(" ")[0]) }}</td>
            <td data-testid="emissao-cell">{{ utils.formatOnlyDate(cobranca.boleto.open_date.split(" ")[0]) }}</td>
            <td data-testid="actions-cell">
              <div class="actions" data-testid="actions-container">
                <div data-testid="btn-detalhes-container">
                  <IconButton @handle:click="() => openDetails(cobranca)" color="secondary" title="Detalhes"
                    :disabled="loadingActions" data-testid="btn-detalhes">
                    <InformationOutline :size="19" />
                  </IconButton>
                </div>
                <div v-if="['pending', 'paid', 'overdue'].includes(cobranca.boleto.status)"
                  data-testid="btn-download-container">
                  <IconButton @handle:click="() => ticketPDFDownlaod(cobranca.id)" color="success" title="Baixar boleto"
                    :disabled="loadingActions" data-testid="btn-download-boleto">
                    <FileOutline :size="19" />
                  </IconButton>
                </div>

                <template v-if="idRole === 6">
                  <div v-if="['pending', 'overdue'].includes(cobranca.boleto.status)"
                    data-testid="btn-notificacao-container">
                    <IconButton @handle:click="() => sendNotification(cobranca.id)" color="primary"
                      title="Enviar notificação" :disabled="loadingActions" data-testid="btn-enviar-notificacao">
                      <SendCircleOutline :size="19" />
                    </IconButton>
                  </div>
                  <div v-if="['pending', 'overdue'].includes(cobranca.boleto.status)"
                    data-testid="btn-cancelar-container">
                    <IconButton @handle:click="() => openDeleteModal(cobranca.id)" color="danger"
                      title="Cancelar cobrança" :disabled="loadingActions" data-testid="btn-cancelar-cobranca">
                      <Cancel :size="19" />
                    </IconButton>
                  </div>
                </template>

                <div v-if="['pending', 'overdue'].includes(cobranca.boleto.status)"
                  data-testid="btn-atualizar-container">
                  <IconButton @handle:click="() => updateBolecode(cobranca.id)" color="primary"
                    title="Atualizar cobrança" :disabled="loadingActions" data-testid="btn-atualizar-boleto">
                    <Reload :size="19" />
                  </IconButton>
                </div>
                <div v-if="cobranca.boleto.status === 'cancelled' && canReactivate()"
                  data-testid="reativar-boleto-container">
                  <IconButton @handle:click="() => openReativarModal(cobranca.id)" color="success"
                    title="Reativar boleto" :disabled="loadingActions" data-testid="btn-reativar-boleto">
                    <Cached :size="19" />
                  </IconButton>
                </div>
              </div>
            </td>
          </tr>
        </Table>
      </div>
    </Box>

    <CobrancaInfos ref="cobrancaInfos" data-testid="modal-cobranca-infos" />

    <ConfirmModal ref="confirmModal" title="Cancelar cobrança"
      message="Tem certeza que deseja cancelar essa cobrança? esta ação é irreversível e a cobrança não poderá ser recuperada!"
      :onConfirm="cancel" data-testid="modal-cancelar-cobranca" />

    <ConfirmModal ref="reativarModal" title="Reativar Boleto" message="Tem certeza que deseja reativar este boleto?"
      :onConfirm="reativarBoleto" data-testid="modal-reativar-boleto" />
  </PageContainer>
</template>

<script>
import PageContainer from 'core/PageContainer'
import Box from 'core/Box'
import Button from 'core/Button'
import LoadingSpinner from 'core/LoadingSpinner'
import Select from 'core/Select'
import Paginate from 'core/Paginate'
import Table from 'core/Table'
import IconButton from 'core/IconButton'
import BadgeCircle from 'core/BadgeCircle'
import Period from 'core/Period'
import ConfirmModal from 'core/ConfirmModal'
import Tooltip from 'core/Tooltip'

import CloseCircleOutline from 'icons/CloseCircleOutline'
import Magnify from 'icons/Magnify'
import FileOutline from 'icons/FileOutline'
import InformationOutline from 'icons/InformationOutline'
import SendCircleOutline from 'icons/SendCircleOutline'
import Cancel from 'icons/Cancel'
import Reload from 'icons/Reload'
import Cached from 'icons/Cached'

import utils from '../../services/utils'
import _export from '../../services/export'
import CobrancaInfos from './CobrancaInfos'

export default {
  name: "BuscarCobrancas",
  components: {
    PageContainer,
    Box,
    Button,
    LoadingSpinner,
    Select,
    Paginate,
    Table,
    IconButton,
    BadgeCircle,
    Period,
    ConfirmModal,
    Tooltip,

    CloseCircleOutline,
    Magnify,
    FileOutline,
    InformationOutline,
    SendCircleOutline,
    Cancel,
    Reload,
    Cached,

    CobrancaInfos,
  },
  data() {
    return {
      idRole: Number(localStorage.getItem('id_role')),
      utils: utils,
      schema: null,
      status: null,
      initialDate: '',
      finalDate: '',
      paginationData: {
        currentPage: 1,
        lastPage: 0,
        total: 0,
      },
      schemaOptions: [],
      statusOptions: [
        { value: 'pending', text: 'Pendente' },
        { value: 'paid', text: 'Pago' },
        { value: 'cancelled', text: 'Cancelado' },
        { value: 'overdue', text: 'Vencido' },
      ],
      cobrancas: [],
      loading: false,
      loadingActions: false,
      selectedCobrancaId: null,
      cancelamentoInfos: {},
    }
  },
  computed: {
    headers() {
      if (this.idRole === 6) {
        return ['Auto escola', 'Valor', 'Status', 'Vencimento', 'Emissão', 'Ações']
      }
      return ['Valor', 'Status', 'Vencimento', 'Emissão', 'Ações']
    }
  },
  async created() {
    if (this.idRole === 6) {
      await Promise.all([
        this.fetchCfcs(),
        this.fetchCobrancas()
      ])
    } else {
      await this.fetchCobrancas()
    }

  },
  methods: {
    async fetchCfcs() {
      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/autoescola/todas`)
        .then((res) => {
          this.schemaOptions = res.body.dados.map(cfc => ({ value: cfc.id_cfc, text: cfc.nome_fantasia }))
        })
        .catch((error) => {
          this.$toasted.show('Erro!' + error.body.mensagem, {
            position: 'top-right',
          })
        })
    },
    async fetchCobrancas(page = 1) {
      this.loading = true

      const params = {
        page,
      }

      if (this.schema) {
        params.schema = this.schema
      }

      if (this.status) {
        params.status = this.status
      }

      if (this.initialDate && this.finalDate) {
        params.initialDate = this.initialDate
        params.finalDate = this.finalDate
      }

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/cobrancas`, {
        params,
      })
        .then(response => {
          const result = response.body.dados

          if (result.data.length === 0) {
            this.$toasted.show('Nenhum resultado encontrado.', {
              icon: 'fa-times',
              position: 'top-right',
            })

            this.cobrancas = []

            this.paginationData = {
              currentPage: 1,
              lastPage: 1,
              total: 0,
            }

            return
          }

          this.cobrancas = result.data

          this.paginationData = {
            currentPage: result.current_page,
            lastPage: result.last_page,
            total: result.total,
          }
        })
        .catch(err => {
          this.$toasted.show('Erro ao buscar autoescolas.', {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loading = false
        })
    },
    async handleSubmit() {
      if (this.initialDate && this.finalDate) {
        if (this.initialDate > this.finalDate) {
          this.$toasted.show('A data inicial não pode ser maior que a data final.', {
            icon: 'fa-times',
            position: 'top-right',
          })
          return
        }
      }

      await this.fetchCobrancas(this.paginationData.currentPage)
    },
    clearInputs() {
      this.schema = null
      this.status = null
      this.initialDate = ''
      this.finalDate = ''
    },
    async ticketPDFDownlaod(id) {
      this.loadingActions = true

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/cobrancas/${id}/download`, {
        responseType: 'blob',
      })
        .then(response => response.blob())
        .then(response => {
          _export.downloadPDFBlob(response, 'boleto')
        })
        .catch(err => {
          this.$toasted.show('Erro ao tentar baixar o boleto.', {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    openDetails(cobranca) {
      this.$refs.cobrancaInfos.open(cobranca)
    },
    async sendNotification(id) {
      this.loadingActions = true

      await this.$http.get(`${process.env.VUE_APP_URL_API_V2}/cobrancas/${id}/notification`)
        .then(response => {
          this.$toasted.show('Notificação enviada com sucesso!', {
            icon: 'fa-check',
            position: 'top-right',
          })
        })
        .catch(err => {
          this.$toasted.show('Erro ao tentar enviar a notificação.', {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async cancel(id) {
      this.loadingActions = true

      this.$refs.confirmModal.close()

      await this.$http.put(`${process.env.VUE_APP_URL_API_V2}/cobrancas/${id}/cancel`)
        .then(response => {
          this.fetchCobrancas(this.paginationData.currentPage)

          this.$toasted.show('Cobrança cancelada com sucesso!', {
            icon: 'fa-check',
            position: 'top-right',
          })
        })
        .catch(err => {
          this.$toasted.show('Erro ao tentar cancelar a cobrança.', {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    openDeleteModal(id) {
      this.$refs.confirmModal.open(id)
    },
    async updateBolecode(id) {
      this.loadingActions = true

      await this.$http.put(`${process.env.VUE_APP_URL_API_V2}/cobrancas/${id}/update-bolecode`)
        .then(response => {
          this.fetchCobrancas(this.paginationData.currentPage)

          this.$toasted.show(response.body.mensagem, {
            icon: 'fa-check',
            position: 'top-right',
          })
        })
        .catch(err => {
          this.$toasted.show('Erro ao tentar atualizar o boleto.', {
            icon: 'fa-times',
            position: 'top-right',
          })

          this.$toasted.show(err.body.mensagem, {
            icon: 'fa-times',
            position: 'top-right',
          })
        })
        .finally(() => {
          this.loadingActions = false
        })
    },
    async loadCancelamentoInfo(cobrancaId) {
      // Se já temos a informação, não faz nova requisição
      if (this.cancelamentoInfos[cobrancaId]) {
        return
      }

      try {
        const response = await this.$http.get(`${process.env.VUE_APP_URL_API_V1}/boletos/logs/last-cancellation`, {
          params: { cobranca_id: cobrancaId }
        })

        if (response.body.sucesso) {
          const info = response.body.dados
          this.$set(this.cancelamentoInfos, cobrancaId, info)
        }
      } catch (error) {
        console.error('Erro ao buscar informações de cancelamento:', error)
        this.$set(this.cancelamentoInfos, cobrancaId, {
          usuario: 'Desconhecido',
          data: '-',
          hora: '-'
        })
      }
    },
    canReactivate() {
      const user = this.$store.getters['auth/user']
      // Função 6 = Financeiro, Função 9 = Monitoramento
      return user && [6, 9].includes(user.id_funcao_usuario)
    },
    openReativarModal(cobrancaId) {
      this.selectedCobrancaId = cobrancaId
      this.$refs.reativarModal.open(cobrancaId)
    },
    async reativarBoleto() {
      this.loadingActions = true

      try {
        const response = await this.$http.post(`${process.env.VUE_APP_URL_API_V2}/cobrancas/reativar-boleto`, {
          cobranca_id: this.selectedCobrancaId
        })

        if (response.body.sucesso) {
          this.$toasted.show('Boleto reativado com sucesso!', {
            position: 'top-right',
            type: 'success'
          })

          // Atualizar lista de cobranças
          await this.fetchCobrancas(this.paginationData.currentPage)

          // Limpar cache de cancelamento
          this.$delete(this.cancelamentoInfos, this.selectedCobrancaId)
        }
      } catch (error) {
        this.$toasted.show(`Erro ao reativar boleto: ${(error.body && error.body.mensagem) || error.message}`, {
          position: 'top-right',
          type: 'error'
        })
      } finally {
        this.loadingActions = false
        this.$refs.reativarModal.close()
        this.selectedCobrancaId = null
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.loading {
  display: flex;
  justify-content: center;
  align-items: center;
}

.cobrancas-container {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;

  form {
    display: flex;
    flex-direction: column;
    gap: 2.5rem;

    .inputs {
      display: flex;
      flex-direction: column;
      gap: 1.5rem;

      .inputs-row {
        display: flex;
        flex-wrap: wrap;
        gap: 1.5rem;

        .input-full {
          flex: 1;
        }
      }
    }

    .buttons {
      align-self: flex-end;

      display: flex;
      gap: 0.5rem;
    }
  }

  .table-container {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    .pagination-container {
      align-self: flex-end;
    }

    .table-container {
      display: flex;
      flex-direction: column;
      gap: 1rem;

      .infos {
        display: flex;
        flex-wrap: wrap;
        align-items: flex-end;
        gap: 1.5rem;
      }

      .table-header {
        display: flex;
        flex-wrap: wrap;
        justify-content: space-between;
        align-items: center;
        gap: 1rem;

        .table-legend {
          display: flex;
          flex-wrap: wrap;
          align-items: center;
          gap: 0.625rem;

          >div {
            display: flex;
            align-items: center;
            gap: 0.5rem;
          }
        }
      }

      .actions {
        max-width: 8rem;
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        row-gap: 0.5rem;

        div {
          padding: 0 0.5rem;
          display: flex;
          justify-content: center;

          &:not(:last-child) {
            border-right: 1px solid #ECECEC;
          }
        }
      }
    }
  }
}

.status-cancelado {
  cursor: help;
  text-decoration: underline;
  text-decoration-style: dotted;
}
</style>